import { Button, Icon, Modal } from '@aidkitorg/component-library';
import React, { useCallback, useMemo } from 'react';
import { useLocalizedStrings } from '../../../Localization';
import { getTodayAsYYYYMMDD, toPascalCase } from '../../../utils/adminPanel.utils';
import type { NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import * as XLSX from 'xlsx';
import { usePost } from '../../../API';

type NotificationRecipientsDownloadModalProps = {
  notificationName: string;
  sentData: NotificationRecipientsResult['sent'];
  eligibleData: NotificationRecipientsResult['eligible'];
  variables: string[];
  downloadModalOpen: boolean;
  setDownloadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NotificationRecipientsDownloadModal: React.FC<NotificationRecipientsDownloadModalProps> = ({
  notificationName,
  sentData,
  eligibleData,
  variables,
  downloadModalOpen,
  setDownloadModalOpen,
}) => {
  const L = useLocalizedStrings();
  const logAudit = usePost("/api/admin_panel/log_audit_entry");

  const formattedNotificationName = toPascalCase(notificationName);

  const logDownloadEvent = async (format: string, type?: 'sent' | 'eligible') => {
    try {
      await logAudit({
        eventName: "NOTIFICATION_RECIPIENTS_DOWNLOADED",
        value: {
          notificationName: formattedNotificationName,
          format: format,
          type: type,
          sentCount: sentData.length,
          eligibleCount: eligibleData.length,
        }
      });
    } catch (error) {
      console.error('Failed to log notification recipients download:', error);
    }
  };
  
  const processDataForSheet = useCallback((data: NotificationRecipientsResult['sent'] | NotificationRecipientsResult['eligible'], includeStatusFields: boolean) => {
    return data.map((recipient) => {
      const baseData = {
        uid: recipient.uid,
        legal_name: recipient.legal_name,
        channel: recipient.channel,
        message: recipient.message,
        subject: recipient.subject,
        ...(includeStatusFields && {
          status: recipient.status || '',
          sent_timestamp: recipient.sentTimestamp ? new Date(recipient.sentTimestamp).toLocaleString() : ''
        }),
      };

      // Add variables as separate columns
      const variableData: Record<string, string | undefined> = {};
      variables.forEach(variable => {
        variableData[variable] = recipient.variables?.[variable] || '';
      });

      return { ...baseData, ...variableData };
    });
  }, [variables]);

  const processedSentData = useMemo(() => processDataForSheet(sentData, true), [sentData, processDataForSheet]);
  const processedEligibleData = useMemo(() => processDataForSheet(eligibleData, false), [eligibleData, processDataForSheet]);

  const downloadCSV = useCallback(async (data: Record<string, string | undefined>[], type: 'sent' | 'eligible') => {
    const convertToCSV = (objArray: Record<string, string | undefined>[]): string => {
      if (objArray.length === 0) return '';
      
      const array = [Object.keys(objArray[0])].concat(objArray.map(obj => Object.values(obj).map(val => val ?? '')));
      return array
        .map(row =>
          row.map(String).map(val => `"${val.replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
    };

    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const typeLabel = type === 'sent' ? L.admin_panel.notifications.sent : L.admin_panel.notifications.eligible;
    const fileName = `${formattedNotificationName}_${typeLabel}_Recipients_${getTodayAsYYYYMMDD()}.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    await logDownloadEvent('CSV', type);
  }, [formattedNotificationName, L.admin_panel.notifications.sent, L.admin_panel.notifications.eligible, logDownloadEvent]);

  const downloadSentCSV = useCallback(async () => {
    await downloadCSV(processedSentData, 'sent');
  }, [downloadCSV, processedSentData]);

  const downloadEligibleCSV = useCallback(async () => {
    await downloadCSV(processedEligibleData, 'eligible');
  }, [downloadCSV, processedEligibleData]);

  const downloadAsXLSX = useCallback(async () => {
    const workbook = XLSX.utils.book_new();
    
    // Create sent recipients sheet
    const sentWorksheet = XLSX.utils.json_to_sheet(processedSentData);
    XLSX.utils.book_append_sheet(workbook, sentWorksheet, 'Sent Recipients');
    
    // Create eligible recipients sheet
    const eligibleWorksheet = XLSX.utils.json_to_sheet(processedEligibleData);
    XLSX.utils.book_append_sheet(workbook, eligibleWorksheet, 'Eligible Recipients');

    const fileName = `${formattedNotificationName}_Recipients_${getTodayAsYYYYMMDD()}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    await logDownloadEvent('XLSX');
  }, [processedSentData, processedEligibleData, formattedNotificationName, logDownloadEvent]);

  return (
    <Modal 
      title={L.admin_panel.notifications.export_recipients} 
      open={downloadModalOpen} 
      onClose={() => setDownloadModalOpen(false)}
    >
      <div className="pb-4">
        {L.admin_panel.notifications.export_recipients_desc}
      </div>
      <div className="flex flex-col gap-y-4">
        {/* Excel Export Section */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">{L.admin_panel.notifications.xlsx_format}</div>
          <Button onClick={downloadAsXLSX} variant="secondary" className="!justify-start w-full">
            <div className="flex gap-4 items-center py-2">
              <Icon name="TableCellsIcon" role='decorative'/>
              <div className="!text-left"> 
                <div>{L.admin_panel.notifications.xlsx_format}</div>
                <div className="font-light">{L.admin_panel.notifications.xlsx_format_desc}</div>
              </div>
            </div>
          </Button>
        </div>

        {/* CSV Export Section */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">{L.admin_panel.notifications.csv_format}</div>
          <div className="flex flex-col gap-y-2">
            <Button 
              onClick={downloadSentCSV} 
              variant="secondary" 
              className="!justify-start w-full"
              disabled={processedSentData.length === 0}
            >
              <div className="flex gap-4 items-center py-2">
                <Icon name="ArrowDownTrayIcon" role='decorative'/>
                <div className="!text-left"> 
                  <div>{L.admin_panel.notifications.csv_format} - {L.admin_panel.notifications.sent}</div>
                  <div className="font-light">{L.admin_panel.notifications.csv_format_desc}</div>
                </div>
              </div>
            </Button>
            <Button 
              onClick={downloadEligibleCSV} 
              variant="secondary" 
              className="!justify-start w-full"
              disabled={processedEligibleData.length === 0}
            >
              <div className="flex gap-4 items-center py-2">
                <Icon name="ArrowDownTrayIcon" role='decorative'/>
                <div className="!text-left"> 
                  <div>{L.admin_panel.notifications.csv_format} - {L.admin_panel.notifications.eligible}</div>
                  <div className="font-light">{L.admin_panel.notifications.csv_format_desc}</div>
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};